import { getSupabaseSession } from "src/supabase/supabaseClient";
import { api } from "./api";
import { BASE_URL } from "../constants";

export type ToolType = {
  callId: string;
  name: string;
  args?: string;
  output?: string;
  failed?: boolean;
};

type StreamEvent =
  | { kind: "token"; text: string }
  | { kind: "tool_call"; callId: string; name: string; args: string }
  | {
      kind: "tool_output";
      callId: string;
      name: string;
      output: { type: string; text: string };
      failed: boolean;
    }
  | { kind: "done"; answer: string }
  | { kind: "error"; message: string };

function parseFrame(frame: string): StreamEvent | null {
  const dataLine = frame.split("\n").find((l) => l.startsWith("data: "));
  if (!dataLine) return null;
  try {
    return JSON.parse(dataLine.slice(6)) as StreamEvent;
  } catch {
    return null;
  }
}

type Response = {
  answer: string;
  question: string;
  status: string | null;
  done: boolean;
  error: string;
  tools: Record<string, ToolType>;
  callOrder: string[];
};

export const askApi = api.injectEndpoints({
  endpoints: (builder) => ({
    submitQuestion: builder.query<
      Record<string, Response>,
      {
        question: string;
        conversationId: string;
        agent: "knowledge" | "vida";
        file: File | null;
      }
    >({
      queryFn: (args) => ({
        data: {
          [args.question]: {
            answer: "",
            status: null,
            done: false,
            question: args.question,
            error: "",
            tools: {},
            callOrder: [],
          },
        },
      }),

      async onCacheEntryAdded(
        args,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded;

        const controller = new AbortController();

        try {
          const session = await getSupabaseSession();

          if (session == null) {
            throw new Error("Unauthorized");
          }

          const endpoint = args.agent == "vida" ? "" : `/${args.agent}`;
          const form = new FormData();
          form.append("question", args.question);
          form.append("conversationId", args.conversationId);
          form.append("agent", args.agent);
          if (args.file != null) {
            form.append("file", args.file, args.file.name);
          }

          const res = await fetch(`${BASE_URL}/api/ask/vida${endpoint}`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
            body: form,
            signal: controller.signal,
          });

          if (res.body == null) {
            return;
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split("\n\n");
            buffer = frames.pop() ?? "";

            for (const frame of frames) {
              const evt = parseFrame(frame);
              if (!evt) continue;

              updateCachedData((draft) => {
                switch (evt.kind) {
                  case "token":
                    draft[args.question].answer += evt.text;
                    break;
                  case "tool_call": {
                    const existing = draft[args.question].tools[evt.callId];
                    if (!existing)
                      draft[args.question].callOrder.push(evt.callId);
                    draft[args.question].tools[evt.callId] = {
                      callId: evt.callId,
                      name: evt.name,
                      args: evt.args,
                      output: existing?.output,
                    };
                    break;
                  }
                  case "tool_output": {
                    const existing = draft[args.question].tools[evt.callId];
                    if (!existing)
                      draft[args.question].callOrder.push(evt.callId);
                    draft[args.question].tools[evt.callId] = {
                      callId: evt.callId,
                      name: existing?.name ?? evt.name,
                      args: existing?.args,
                      output: evt.output.text,
                      failed: evt.failed,
                    };
                    break;
                  }
                  case "done":
                    draft[args.question].answer = evt.answer; // authoritative
                    draft[args.question].done = true;
                    break;
                  case "error":
                    draft[args.question].error = evt.message;
                    draft[args.question].done = true;
                    break;
                }
              });
            }
          }

          updateCachedData((draft) => {
            if (!draft[args.question].done) draft[args.question].done = true;
          });
        } catch {
          updateCachedData((draft) => {
            draft[args.question].error = "Stream ended unexpected";
            draft[args.question].done = true;
          });
        }

        await cacheEntryRemoved; // stream tied to cache lifetime
        controller.abort(); // abort fetch if component unmounts
      },
    }),
  }),
});

export const { useSubmitQuestionQuery } = askApi;
